import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxes, FaBoxOpen, FaCamera, FaCartPlus, FaCheck, FaCog, FaEdit, FaFileDownload, FaFileUpload, FaImage, FaList, FaPlus, FaQrcode, FaSearch, FaSync, FaTag, FaTh, FaTimes, FaTrash } from 'react-icons/fa';
import { Badge, Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { productActions } from '../../sagas/productSlice';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import config from '../../helpers/config';
import { PRODUCTS_FORM_PAGE } from '../../routes/routeConstants';
import { useLocation, useNavigate } from 'react-router-dom';
import { categoryActions } from '../../sagas/categorySlice';
import { formatPrice } from '../../helpers/utility';
import Select from 'react-select';
import axiosInstance from '../../services/api_helper';

const ProductList = () => {
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const [displayMode, setDisplayMode] = useState('table'); // or 'card'
  const [currentPage, setCurrentPage] = useState(1);
  const [labelModal, setLabelModal] = useState(false);
  const [generatingLabels, setGeneratingLabels] = useState(false);
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites
  const { list } = useSelector(state => state.categories); // Assuming you have a Redux store with categories

  const [labelOptions, setLabelOptions] = useState({
    scope: "FILTERS",        // FILTERS | PAGE
    labelFormat: "50x30",    // 50x30 | 60x40 | 38x25 | 40x30 | 70x50
    barcodeType: "CODE128",  // CODE128 | EAN13
    showPrice: false,
    showCode: true,
    qtyPerArticle: 1
  });

  const [filters, setFilters] = useState({
    categorieId: null,
    query: ''
  });

  const {
    products,
    loading,
    error,
    totalElements,
    totalPages,
    pageNumber,
    pageSize
  } = useSelector((state) => state.products);
  const navigate = useNavigate();

  useEffect(() => {
    // Auto focus on mount
    searchInputRef.current?.focus();

    // Add keyboard shortcut
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [products]);

  useEffect(() => {
    dispatch(categoryActions.list({ query: '' })); // Fetch categories on component mount
    dispatch(productActions.findAllUnites());

  }, []);


  useEffect(() => {
    dispatch(productActions.findAllArticles({
      ...filters,
      page: currentPage - 1,
      size: 10,
    }));

  }, [currentPage, filters.categorieId]);


  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };


  const handleGenerateLabelsPdf = async () => {
    try {
      setGeneratingLabels(true);

      // Build request payload
      const payload = {
        scope: labelOptions.scope,
        labelFormat: labelOptions.labelFormat,
        barcodeType: labelOptions.barcodeType,
        showPrice: labelOptions.showPrice,
        showCode: labelOptions.showCode,
        layout: labelOptions.layout,
        qtyPerArticle: Number(labelOptions.qtyPerArticle) || 1,

        // filters from current UI
        query: filters.query || "",
        categorieId: filters.categorieId || null,

        // if scope === PAGE => use current page products ids
        productIds: labelOptions.scope === "PAGE" ? (products || []).map(p => p.id) : []
      };

      // Call backend endpoint that returns PDF as blob
      const response = await axiosInstance.post(
        `${config.baseURL}/api/articles/barcodes/labels/pdf`,
        payload,
        { responseType: "blob" }
      );

      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(response.data, `etiquettes_${today}.pdf`);

      Swal.fire({
        icon: "success",
        title: "PDF généré",
        text: "Le fichier des étiquettes a été téléchargé avec succès.",
        confirmButtonColor: "#198754",
      });

      setLabelModal(false);
    } catch (e) {
      console.error(e);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de générer le PDF des étiquettes.",
      });
    } finally {
      setGeneratingLabels(false);
    }
  };


  // Determine the correct client form path based on current context
  const getProductFormPath = () => {
    // If we're in a sales module route, use relative path
    if (location.pathname.startsWith('/inventory-module')) {
      return '/inventory-module/products/new';
    }
    // Otherwise use the legacy absolute path
    return PRODUCTS_FORM_PAGE;
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      debouncedSearch(); // Trigger search when Enter is pressed
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, query: value }));
  };

  // Debounced search function
  const debouncedSearch = () => {
    dispatch(productActions.findAllArticles({
      ...filters,
      page: 0,
      size: 10,
    }));
  }



  const refrechProducts = () => {
    setIsSearching(true);
    dispatch(productActions.findAllArticles({
      ...filters,
      page: 0,
      size: 10,
    }));
  }


  const handleDelete = (productId) => {
    Swal.fire({
      title: t('product.deleteConfirmTitle'),
      text: t('product.deleteConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: t('common.confirm'),
      cancelButtonText: t('common.cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {

          let payload = {
            id: productId,
            onSuccess: () => {
              Swal.fire(t('product.deleted'), '', 'success');
              refrechProducts();
            },
            onError: (error) => {
              Swal.fire({
                icon: 'error',
                title: t('product.deleteError'),
                text: t('product.deleteErrorMessage')
              });
            }
          }
          dispatch(productActions.deleteProduct(payload));

        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: t('common.error'),
            text: t('product.deleteError')
          });
        }
      }
    });
  };

  const handleActiverArticle = (productId) => {
    Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: "Le produit sera activé et pourra être utilisé.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, activer !',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          id: productId,
          onSuccess: () => {
            Swal.fire(
              'Activé !',
              'Le produit a été activé avec succès.',
              'success'
            );
            refrechProducts();
          },
          onError: () => {
            Swal.fire(
              'Erreur !',
              'Une erreur s\'est produite lors de l\'activation.',
              'error'
            );
          }
        }
        dispatch(productActions.activate(payload));
      }
    });
  };



  const handleEdit = (product) => {
    navigate("/inventory-module/products/edit", { state: { product } });
  };



  const downloadTemplate = () => {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const headers = [
      [
        'Code',
        'Code-barres',
        'Libellé',
        'Type d\'article',
        'Catégorie',
        'Unité',
        'Prix d\'achat TTC',
        'TVA Achat (%)',
        'TVA Vente (%)',
        'Prix Vente HT',
        'Prix Vente TTC',
        'Stock Minimal',
        'Stock Physique',
        'Stock Disponible'
      ]
    ];

    // Create worksheet with headers
    const ws = XLSX.utils.aoa_to_sheet(headers);

    // Set column widths
    const colWidths = [
      { wch: 15 },  // Code
      { wch: 20 },  // Code-barres
      { wch: 30 },  // Libellé
      { wch: 15 },  // Type d'article
      { wch: 20 },  // Catégorie
      { wch: 15 },  // Unité
      { wch: 15 },  // Prix d'achat TTC
      { wch: 15 },  // TVA Achat (%)
      { wch: 15 },  // TVA Vente (%)
      { wch: 15 },  // Prix Vente HT
      { wch: 15 },  // Prix Vente TTC
      { wch: 15 },  // Stock Minimal
      { wch: 15 },  // Stock Physique
      { wch: 15 }   // Stock Disponible
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Products Template");

    // Generate and download file
    XLSX.writeFile(wb, "products_template.xlsx");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: 'error',
          title: t('product.invalidFileType'),
          text: t('product.excelOnly'),
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        let payload = {
          formData: formData,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('product.uploadSuccess'),
              confirmButtonText: 'OK',
              confirmButtonColor: '#198754'
            });
            refrechProducts();
          },
        }
        dispatch(productActions.uploadProducts(payload));

      } catch (error) {
        console.error('Error parsing Excel:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur!',
          text: "Erreur lors de l'importation des produits",
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      } finally {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  }

  const DisplayToggle = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="btn-group">
        {/* <Button
          color={displayMode === 'table' ? 'primary' : 'light'}
          onClick={() => setDisplayMode('table')}
          size="sm"
        >
          <FaList className="me-1" /> {t('common.table')}
        </Button>
        <Button
          color={displayMode === 'card' ? 'primary' : 'light'}
          onClick={() => setDisplayMode('card')}
          size="sm"
        >
          <FaTh className="me-1" /> {t('common.cards')}
        </Button> */}
      </div>

      <div className="d-flex align-items-center gap-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>

        <div className="d-flex align-items-center gap-2">
          <Button color="light" size="sm" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <FaAngleDoubleLeft />
          </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <FaAngleLeft />
          </Button>

          <span className="mx-2">
            {currentPage} / {totalPages}
          </span>

          <Button color="light" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            <FaAngleRight />
          </Button>
          <Button color="light" size="sm" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
            <FaAngleDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  );

  const TableView = () => (
    <>
      <div className="cart-container" style={{
        height: '600px',
        overflowY: 'auto',
        fontSize: 12
      }}>
        <Table hover bordered className="align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th className="align-middle">
                <FaBox className="me-2 text-primary" size={12} />
                {t('product.category')}
              </th>
              <th className="align-middle">
                <FaBarcode className="me-2 text-primary" size={12} />
                {t('product.code')}
              </th>
              <th className="align-middle">
                <FaBarcode className="me-2 text-primary" size={12} />
                {t('product.barcode')}
              </th>
              <th className="align-middle">
                <FaBox className="me-2 text-primary" size={12} />
                {t('product.description')}
              </th>
              <th className="align-middle">
                <FaBox className="me-2 text-primary" size={12} />
                {t('devis.unite')}
              </th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.salePrice')}
              </th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.buyingPrice')}
              </th>
              <th className="align-middle text-end">
                <FaTag className="me-2 text-primary" size={12} />
                {t('product.stockDisponible')}
              </th>

              <th className="align-middle text-center">{t('client.active')}</th>
              <th className="align-middle text-center" style={{ width: '120px' }}>
                <FaCog className="me-2 text-primary" size={12} />
                {t('common.actions')}
              </th>
            </tr>
          </thead>

          <tbody>
            {!products || products.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <div className="text-muted">
                    {t('product.noProducts')}
                  </div>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr key={product.id}>
                  <td className="align-middle">{product.categorieLibelle}</td>
                  <td className="align-middle">{product.code}</td>
                  <td className="align-middle">{product.codeBarre}</td>
                  <td className="align-middle">
                    {product.libelle}
                  </td>
                  <td>
                    {unites.find(u => u.code === product.unite)?.libelle}
                  </td>
                  <td className="align-middle text-end">
                    {formatPrice(product.prixVenteTTC1)}
                  </td>
                  <td className="align-middle text-end">{formatPrice(product.prixAchatTTC)}</td>
                  <td className="align-middle">{product.stockDisponible}</td>

                  <td>
                    {product.actif ? (
                      <Badge color="success">Actif</Badge>
                    ) : (
                      <Badge color="danger">Bloqué</Badge>
                    )}
                  </td>
                  <td className="align-middle text-center">
                    <Button color="warning" size="sm" className="me-1 px-2 py-1">
                      <FaEdit size={12} onClick={() => handleEdit(product)} />
                    </Button>


                    {product.actif ? <Button color="danger" size="sm" className="px-2 py-1">
                      <FaTrash size={12} onClick={() => handleDelete(product.id)} />
                    </Button>
                      :
                      <Button
                        className="ms-1"
                        color="success"
                        size="sm"
                        title='Activer'
                        onClick={() => handleActiverArticle(product.id)}
                      >
                        <FaCheck />
                      </Button>
                    }
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>
      </div>
    </>
  );





  const CardView = () => {
    return (
      <div className="cart-container" style={{ height: '600px', overflowY: 'auto' }}>
        <Row xs={2} md={4} lg={5} className="g-3">
          {(!products || products.length === 0) && (
            <Col xs={12} className="text-center py-4 ">
              {t('product.noProducts')}
            </Col>
          )}
          {
            products.map(product => (
              <Col key={product.id}>
                <Card className="product-card rounded border shadow-sm">
                  <div className="card-image-container">
                    {product.photo ? (
                      <img
                        src={`${config.baseURL}${product.photo}`}
                        alt={product.libelle}
                        className="card-img-top rounded-top"
                      />
                    ) : (
                      <div className="bg-placeholder rounded-top d-flex align-items-center justify-content-center">
                        <FaImage size={32} className="text-muted" />
                      </div>
                    )}
                  </div>
                  <CardBody className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="product-title mb-2">
                        {product.libelle}
                      </h6>
                      <div className="action-buttons d-flex">
                        <Button color="warning" size="sm" className="me-1" onClick={() => handleEdit(product)}>
                          <FaEdit size={12} />
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleDelete(product.id)}>
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    </div>

                    <p className="product-price mb-1">
                      {product.unite ? `${formatPrice(product.prixVenteTTC1)} / ${product.unite}` : 'N/A'}
                    </p>

                  </CardBody>
                </Card>
              </Col>
            ))
          }
        </Row>
      </div>
    );
  };



  return (
    <Container fluid>
      <Row>
        <Col md={12} className="pe-4">
          <Card className="shadow-sm h-100">
            <CardBody>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                  <div className="mt-2 text-muted">
                    {t('common.loadingData')}
                  </div>
                </div>
              ) : (<>
                <Row className="mb-3 g-2 align-items-end">
                  <Col md={5}>
                    {/* Search Input - Left Side */}
                    <Input
                      innerRef={searchInputRef}
                      type="text"
                      placeholder={t('product.searchPlaceholder')}
                      value={filters.query}
                      onKeyDown={handleSearchKeyDown}
                      onChange={handleSearchChange}
                      className={`ps-4 ${isSearching ? 'searching' : ''}`}
                    />
                  </Col>
                  <Col md={4}>
                    <Select
                      name="categorie"
                      options={list.map(cat => ({ value: cat.id, label: cat.libelle }))}
                      value={list.map(cat => ({ value: cat.id, label: cat.libelle }))
                        .find(option => option.value === filters.categorieId) || null}
                      onChange={(selected) => setFilters(prev => ({ ...prev, categorieId: selected ? selected.value : null }))}
                      placeholder="Rechercher une catégorie..."
                      isClearable
                      isSearchable
                    />

                  </Col>
                  <Col md={3}>
                    {/* Buttons - Right Side */}
                    <div className="d-flex mx-3">
                      <Button color="primary" size="sm"
                        className="me-2" onClick={() => navigate(getProductFormPath())}>
                        <FaPlus className="me-2" />
                        {t('product.new')}
                      </Button>

                      <Button color="success" size="sm"
                        className="me-2" onClick={() =>
                          dispatch(productActions.updateGlobalStock({
                            onSuccess: () => {
                              refrechProducts()
                            },
                            onError: () => {
                            }
                          }))
                        }>
                        <FaSync className="me-2" />
                        {t('product.refreshStock')}
                      </Button>

                      <Button
                        color="secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => setLabelModal(true)}
                        title="Générer des étiquettes code-barres (PDF)"
                      >
                        <FaBarcode className="me-2" />
                        Étiquettes (PDF)
                      </Button>

                      {/* <Button
                        color="primary"
                        size="sm"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <FaFileUpload className="me-2" size={12} />
                        {t('product.uploadProducts')}
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                      /> */}
                      {/* <Button
                      color="secondary"
                      size="sm"
                      className="me-2"
                      onClick={downloadTemplate}
                    >
                      <FaFileDownload className="me-2" size={12} />
                      {t('product.downloadTemplate')}
                    </Button>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <FaFileUpload className="me-2" size={12} />
                        {t('product.uploadProducts')}
                      </Button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                      />*/}
                    </div>
                  </Col>
                </Row>
                <DisplayToggle />
                {displayMode === 'table' ? <TableView /> : <CardView />}
              </>)}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ===== Barcode Labels Modal (Option A) ===== */}
      <Modal isOpen={labelModal} toggle={() => setLabelModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setLabelModal(false)}>
          <FaBarcode className="me-2" />
          Générer des étiquettes code-barres (PDF)
        </ModalHeader>

        <ModalBody>
          <Row>
            <Input
              type="select"
              value={labelOptions.layout}
              onChange={(e) => setLabelOptions(prev => ({ ...prev, layout: e.target.value }))}
            >
              <option value="SHEET">Planche A4 (plusieurs étiquettes)</option>
              <option value="ROLL">Rouleau (1 étiquette / page)</option>
            </Input>

          </Row>
          <Row className="g-3">
            <Col md={6}>
              <Label className="fw-bold">Portée</Label>
              <Input
                type="select"
                value={labelOptions.scope}
                onChange={(e) => setLabelOptions(prev => ({ ...prev, scope: e.target.value }))}
              >
                <option value="FILTERS">Tous les articles selon filtres (recommandé)</option>
                <option value="PAGE">Articles de la page actuelle</option>
              </Input>
              <small className="text-muted">
                Avec 2750 articles, choisissez <b>"selon filtres"</b>.
              </small>
            </Col>

            <Col md={6}>
              <Label className="fw-bold">Format étiquette</Label>
              <Input
                type="select"
                value={labelOptions.labelFormat}
                onChange={(e) => setLabelOptions(prev => ({ ...prev, labelFormat: e.target.value }))}
              >
                <option value="50x30">50 x 30 mm (recommandé)</option>
                <option value="60x40">60 x 40 mm</option>
                <option value="38x25">38 x 25 mm</option>
                <option value="40x30">40 x 30 mm</option>
                <option value="70x50">70 x 50 mm</option>
              </Input>
            </Col>

            <Col md={6}>
              <Label className="fw-bold">Type de code-barres</Label>
              <Input
                type="select"
                value={labelOptions.barcodeType}
                onChange={(e) => setLabelOptions(prev => ({ ...prev, barcodeType: e.target.value }))}
              >
                <option value="CODE128">CODE128 (idéal interne)</option>
                <option value="EAN13">EAN13 (si codes valides)</option>
              </Input>
            </Col>

            <Col md={6}>
              <Label className="fw-bold">Quantité par article</Label>
              <Input
                type="number"
                min={1}
                value={labelOptions.qtyPerArticle}
                onChange={(e) => setLabelOptions(prev => ({ ...prev, qtyPerArticle: e.target.value }))}
              />
              <small className="text-muted">
                Si vous voulez 2 étiquettes pour chaque article, mettez 2.
              </small>
            </Col>

            <Col md={12}>
              <div className="border rounded p-3 bg-light">
                <div className="d-flex flex-wrap gap-4">
                  <FormGroup check>
                    <Input
                      type="checkbox"
                      checked={labelOptions.showCode}
                      onChange={(e) => setLabelOptions(prev => ({ ...prev, showCode: e.target.checked }))}
                    />
                    <Label check className="ms-2">Afficher Code Article</Label>
                  </FormGroup>

                  <FormGroup check>
                    <Input
                      type="checkbox"
                      checked={labelOptions.showPrice}
                      onChange={(e) => setLabelOptions(prev => ({ ...prev, showPrice: e.target.checked }))}
                    />
                    <Label check className="ms-2">Afficher Prix Vente</Label>
                  </FormGroup>
                </div>
              </div>
            </Col>

            <Col md={12}>
              <div className="alert alert-info mb-0">
                <b>Conseil :</b> pour des imprimantes étiquettes (roll printer), le format <b>50x30</b> ou <b>60x40</b> donne un résultat propre.
              </div>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button color="light" onClick={() => setLabelModal(false)} disabled={generatingLabels}>
            Annuler
          </Button>
          <Button
            color="primary"
            onClick={handleGenerateLabelsPdf}
            disabled={generatingLabels}
          >
            {generatingLabels ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Génération...
              </>
            ) : (
              <>
                <FaFileDownload className="me-2" />
                Générer PDF
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

    </Container>
  );
};

export default ProductList;
